import { GitWriteLock } from "./useGitWriteLock";

/**
 * Decide whether a Console client should raise a failure toast for a git write
 * lock state transition.
 *
 * The git write lock is workspace-global and its WRITE_FAILED state is broadcast
 * over the websocket to EVERY connected client. A toast should be raised only
 * when:
 *   1. this is a NEW failure (transition into WRITE_FAILED, not a repeat), and
 *   2. the write was interactive — initiated by a human in the Console — rather
 *      than by a system/background actor (a running flow / internal
 *      engine/git-sync call: the operator, smoke/integrationtest/scheduled
 *      flows). A system-initiated failure is not something any connected user
 *      caused, so broadcasting it to all of them is noise.
 *
 * `interactive` is emitted by the backend git_write_lock payload. It is absent
 * on payloads from older backends; an absent value is treated as interactive
 * (notify), preserving the prior always-notify behaviour — only an explicit
 * `interactive === false` suppresses.
 *
 * See operator-task-git-write-failed-toast-suppress-system-initiated.
 */
export const shouldNotifyGitWriteFailure = (
    current: GitWriteLock | undefined,
    previous: GitWriteLock | undefined
): boolean => {
    const isNewFailure =
        current?.state === "WRITE_FAILED" && previous?.state !== "WRITE_FAILED";
    const isSystemInitiated = current?.interactive === false;
    return isNewFailure && !isSystemInitiated;
};
